<template>
  <transition name="default-opacity-transition" mode="out-in">
    <s-loader v-if="loading" />
    <div v-else
         class="content__wrapper"
    >
      <b-row>
        <digital-network-modal
          v-model="showModal"
          :asset-id="assetId"
          :company="currentInstallation.companyName"
          :installation="currentInstallation.name"
          :regional="currentInstallation.regionalName"
          :asset="selectedAsset.name"
        />
        <engineering-modules-header
          show-print-icon
          show-treeview-icon
          :show-wifi-icon="this.$hasUserPermission('PER010')"
          :company="currentInstallation.companyName"
          :installation="currentInstallation.name"
          :regional="currentInstallation.regionalName"
          :asset="selectedAsset.name"
          :title="this.$t('page-name.asset-information')"
          :subtitle="subTitle"
        />
      </b-row>
      <div class="px-2 mt-3">
        <router-view
          :asset="currentAsset"
          :asset-type="currentAssetType"
          :sensor="sensor"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { getKeyByValue } from '@/services/objectHelpers';
import loadingMixin from '@/services/loadingMixin';
import AssetAccessorMixin from '@/domain/mixins/installation/assetAccessorMixin';
import sideMenu from '@/domain/data/installation/assetInformation/side-menu.json';
import store from '@/store';
import SubMenuClass from '@/domain/data/menu/SubMenu';
import EngineeringModulesHeader from '@/components/header/EngineeringModulesHeader';
import DigitalNetworkModal from '@/app/installation/supervision/DigitalNetworkModal';
import headerEvents from '@/domain/events/engineeringModulesHeader';
import eventBus from '@/services/globalEventBus';
import { mapGetters } from 'vuex';
import AssetBreadcrumbDecoratorEventBuilder from '../common/assetBreadcrumbDecoratorEventBuilder';
import { assetInformationRouteFeatures } from './config/routes';

export default {
  name: 'AssetInformation',
  components: {
    EngineeringModulesHeader,
    DigitalNetworkModal,
  },
  mixins: [
    loadingMixin,
    AssetAccessorMixin,
  ],
  computed: {
    ...mapGetters([
      'installation',
    ]),
    assetId() {
      const { assetId } = this.$route.params;
      return assetId;
    },
    currentInstallation() {
      return this.installation || {};
    },
    selectedAsset() {
      return this.currentAsset || {};
    },
    assetInformationMenu() {
      return this.sideMenu.find(menuItem => menuItem.id === 'asset-information');
    },
    assetInformationMenuItems() {
      if (!this.currentAsset) return [];
      this.setSubmenu();
      return this.currentAsset.features.map(feature => ({
        name: `asset-information.menu.${feature}`,
        routeName: `asset-information.${feature}`,
        routeParams: {
          feature: getKeyByValue(assetInformationRouteFeatures, feature),
        },
      }));
    },
  },
  created() {
    this.loadAssetInformation();
    this.initListener();
    this.$route.meta.currentPage = this.$route.name;
  },
  beforeDestroy() {
    this.removeListener();
  },
  data: vm => ({
    sideMenu: cloneDeep(sideMenu),
    breadcrumbEvents: AssetBreadcrumbDecoratorEventBuilder(vm, 'asset-information'),
    sensorsList: [],
    sensor: null,
    showModal: false,
    subTitle: '',
  }),
  methods: {
    verifyCurrentRoute(submenuItem) {
      const { name } = this.$route;

      if ((submenuItem || []).some(({ routeName }) => routeName === name)) {
        return;
      }

      this.$router.push({
        name: submenuItem[0].routeName,
        params: submenuItem[0].params,
      });
    },
    loadAssetInformation() {
      this.startLoading();
      this.$set(this.assetInformationMenu, 'items', this.assetInformationMenuItems);
      this.sideMenu = [...this.sideMenu];
      this.endLoading();
    },
    setSubmenu() {
      const { params } = this.$route;
      const subMenuItems = this.currentAsset.features.map(feature => ({
        exact: true,
        label: `asset-information.menu.${feature}`,
        routeName: `monitoring.asset-information.${feature}`,
        type: 'router-link',
        params: {
          ...params,
          ...getKeyByValue(assetInformationRouteFeatures, feature),
        },
      }));

      this.verifyCurrentRoute(subMenuItems);

      const submenu = [{
        groupTitleLabel: 'my-sigma.side-menu.asset-info',
        limitItemsLength: 0,
        items: subMenuItems,
      }].map(item => new SubMenuClass(item));

      store.dispatch('setSubMenuItems', {
        items: submenu,
        maintainPreviousCollapsedState: true,
      });
      store.dispatch('setAssetSubMenu', submenu);
    },
    initListener() {
      eventBus.on(headerEvents.wifi, this.openModal, this);
      eventBus.on(headerEvents.updateTitle, this.setTitle, this);
    },
    removeListener() {
      eventBus.off(headerEvents.wifi, this.openModal, this);
      eventBus.off(headerEvents.updateTitle, this.setTitle, this);
    },
    openModal() {
      this.showModal = true;
    },
    setTitle(subTitle) {
      this.subTitle = subTitle;
    },
  },
  watch: {
    $route() {
      this.$route.meta.currentPage = this.$route.name;
    },
    currentAsset() {
      this.loadAssetInformation();
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.startLoading();
    next();
    if (to.params.assetId !== from.params.assetId) {
      this.loadAssetInformation();
    }
    this.endLoading();
  },
};
</script>

<style lang="sass" scoped>
  .content__wrapper
    padding: 2rem
  ::v-deep
    div.app-content__main-content
      background: var(--color-background-light)
</style>
